import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheck,
  Zap,
  Search,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Smartphone,
  ChevronDown,
  Apple
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const StepCard = ({ number, title, description }) => (
  <div className="bg-white border border-gray-100 p-8 rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-300">
    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center font-bold text-xl mb-6">
      {number}
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const FeatureButton = ({ title, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-6 py-4 rounded-2xl transition-all border ${
      isActive
        ? 'bg-[#141414] text-white border-[#141414] shadow-lg scale-105 z-10'
        : 'bg-white text-gray-600 border-gray-100 hover:bg-gray-50'
    }`}
  >
    <div className="font-bold text-sm">{title}</div>
  </button>
);

const InfoodPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState('scan');
  const [openFaq, setOpenFaq] = useState(null);

  const steps = useMemo(() => t('infood.how_it_works.steps', { returnObjects: true }), [t]);
  const featureItems = useMemo(() => t('infood.features_section.items', { returnObjects: true }), [t]);
  const currentFeature = featureItems[activeFeature];
  const faqs = useMemo(() => t('infood.faq', { returnObjects: true }), [t]);

  return (
    <div className="relative min-h-screen bg-transparent font-inter selection:bg-emerald-100">
      <PageHeader />

      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="text-center mb-24">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-emerald-50 text-emerald-700 mb-6 border border-emerald-100">
              {t('infood.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-[#141414] mb-6 tracking-tight leading-tight">
              {t('infood.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              {t('infood.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {t('infood.pills', { returnObjects: true }).map((pill) => (
                <span key={pill} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  {pill}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#como-funciona"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-4 px-10 rounded-2xl transition shadow-xl"
              >
                {t('infood.cta_how')}
              </a>
              <a
                href="#funciones"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-4 px-10 rounded-2xl transition"
              >
                {t('infood.cta_features')}
              </a>
            </div>
            <p className="mt-6 text-sm text-gray-500 font-medium italic">
              {t('infood.disclaimer')}
            </p>
          </div>

          <section id="como-funciona" className="mb-32 scroll-mt-32">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                {t('infood.how_it_works.badge')}
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-[#141414] mb-6">
                {t('infood.how_it_works.title')}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                {t('infood.how_it_works.desc')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((step, idx) => (
                <StepCard
                  key={step.title}
                  number={idx + 1}
                  title={step.title}
                  description={step.desc}
                />
              ))}
            </div>
          </section>

          <section id="funciones" className="mb-32 scroll-mt-32">
            <div className="bg-[#141414] rounded-[3rem] p-8 md:p-20 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-emerald-600/10 rounded-full blur-[100px] -mr-80 -mt-80" />
              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                <div className="lg:col-span-4 space-y-3">
                  <span className="inline-block px-3 py-1 bg-white/10 text-white/70 text-[10px] font-bold uppercase tracking-widest rounded-full mb-4">
                    {t('infood.features_section.badge')}
                  </span>
                  <h2 className="text-3xl font-bold mb-6">{t('infood.features_section.title')}</h2>
                  <div className="space-y-2">
                    {Object.keys(featureItems).map((key) => (
                      <FeatureButton
                        key={key}
                        title={featureItems[key].title}
                        isActive={activeFeature === key}
                        onClick={() => setActiveFeature(key)}
                      />
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-8">
                  <div className="bg-white rounded-[2.5rem] p-8 md:p-12 text-gray-900 shadow-2xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                      <div>
                        <h3 className="text-2xl font-bold mb-4">{currentFeature.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed mb-8">
                          {currentFeature.desc}
                        </p>
                        <div className="space-y-3">
                          {currentFeature.bullets.map((b) => (
                            <div key={b} className="flex items-center gap-3">
                              <CheckCircle2 size={16} className="text-emerald-500" />
                              <span className="text-xs font-bold text-gray-700">{b}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="relative">
                        <div className="absolute inset-0 bg-emerald-100 rounded-full blur-3xl opacity-30" />
                        <div className="relative bg-gray-50 border border-gray-100 rounded-[2rem] p-6 shadow-inner">
                          <div className="flex items-center justify-between mb-6">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              {t('infood.features_section.mockup_label', { title: currentFeature.title })}
                            </div>
                            <Smartphone size={16} className="text-gray-300" />
                          </div>
                          <div className="space-y-4">
                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                              <div className="text-[8px] font-bold text-gray-400 uppercase mb-2">{t('infood.features_section.mockup_summary')}</div>
                              <div className="text-[10px] text-gray-600 leading-tight">{t('infood.features_section.mockup_summary_desc')}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                                <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">{t('infood.features_section.mockup_level')}</div>
                                <div className="text-xs font-bold text-amber-500">{t('infood.features_section.mockup_level_val')}</div>
                              </div>
                              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm text-center">
                                <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">{t('infood.features_section.mockup_matches')}</div>
                                <div className="text-xs font-bold text-rose-500">{t('infood.features_section.mockup_matches_val')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-32">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-emerald-50 text-emerald-700 mb-6 border border-emerald-100">
                  {t('infood.personalization.badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-6">
                  {t('infood.personalization.title')}
                </h2>
                <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                  {t('infood.personalization.desc')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {t('infood.personalization.profile_chips', { returnObjects: true }).map((chip) => (
                    <span key={chip} className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-emerald-200 hover:text-emerald-600 transition-colors cursor-default">
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-emerald-50 rounded-[3rem] p-12 border border-emerald-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 text-emerald-200">
                  <Apple size={64} />
                </div>
                <div className="relative z-10">
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-4">{t('infood.personalization.active_profile')}</div>
                  <p className="text-sm text-emerald-900/70 leading-relaxed mb-8">{t('infood.personalization.active_desc')}</p>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-emerald-100">
                    <div className="flex items-center gap-3 mb-4">
                      <AlertCircle className="text-rose-500" size={18} />
                      <span className="text-xs font-bold text-gray-900 uppercase tracking-widest">{t('infood.personalization.mockup_matches')}</span>
                    </div>
                    <div className="space-y-2">
                      {t('infood.personalization.mockup_matches_list', { returnObjects: true }).map((m) => (
                        <div key={m} className="text-xs text-rose-600 font-bold bg-rose-50 px-3 py-1.5 rounded-lg border border-rose-100">
                          {m}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-32 grid grid-cols-1 md:grid-cols-3 gap-8">
            {t('infood.trust.cards', { returnObjects: true }).map((card, idx) => (
              <div key={idx} className="bg-white border border-gray-100 p-8 rounded-3xl hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 bg-gray-50 text-gray-400 rounded-lg flex items-center justify-center mb-6">
                  {idx === 0 ? <ShieldCheck size={20} /> : idx === 1 ? <Search size={20} /> : <Zap size={20} />}
                </div>
                <h4 className="font-bold text-gray-900 mb-3">{card.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </section>

          <section className="max-w-3xl mx-auto mb-32">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#141414] mb-4">{t('infood.faq_title')}</h2>
              <p className="text-gray-600">{t('infood.faq_desc')}</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-gray-900">{faq.q}</span>
                    <ChevronDown size={20} className={`text-gray-400 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="text-center bg-emerald-50 rounded-[3rem] p-12 md:p-24 border border-emerald-100">
            <h2 className="text-3xl md:text-5xl font-bold text-[#141414] mb-8 tracking-tight">{t('infood.footer_title')}</h2>
            <p className="text-gray-600 text-lg mb-12 max-w-2xl mx-auto">{t('infood.footer_desc')}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/apps"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-4 px-12 rounded-2xl transition shadow-xl"
              >
                {t('common.all_apps')}
              </Link>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default InfoodPage;
